const sharedSections = {
  generator: [
    {
      label: "Why It Works",
      title: "Templates make the first result easier",
      intro:
        "The fastest way into AI porn generation is to start from a scene that already works. A strong template gives you structure, cuts down guesswork, and gets you to a usable result faster.",
      points: [
        "Start from a look or pose that already fits the scene you want.",
        "Use editing to clean the frame before you generate motion.",
        "Keep the workflow simple when the goal is a fast first result.",
      ],
    },
    {
      label: "How To Use It",
      title: "Still image first, motion second",
      intro:
        "Better clips usually begin as better stills. Clean the image, shape the scene, and then move into image-to-video once the base frame feels strong.",
      points: [
        "Refine the image before asking the model to animate it.",
        "Short clips are easier to keep sharp and consistent.",
        "Reuse the setup that works instead of rebuilding from zero.",
      ],
    },
  ],
  undress: [
    {
      label: "How It Works",
      title: "Start from a cleaner source image",
      intro:
        "Undress AI results feel more convincing when the starting image already has stable framing, clear lighting, and a pose that does not fight the edit.",
      points: [
        "Choose a frame that already looks close to the final direction.",
        "Use compatible edit tools to refine the image gradually.",
        "Keep the background and pose simple when realism matters.",
      ],
    },
    {
      label: "What Looks Better",
      title: "Refine the still before you animate it",
      intro:
        "Most weak results come from trying to fix anatomy, clothing, and motion in one step. It is easier to tighten the still frame first and only then move into video.",
      points: [
        "Clean the image before adding movement.",
        "Use motion as a second step, not the rescue step.",
        "Keep the strongest frame and build variations from there.",
      ],
    },
  ],
  nsfw: [
    {
      label: "Why People Use It",
      title: "One place for templates, edits, and clips",
      intro:
        "The product feels easier when browsing, editing, and generation live in the same flow. You can move from idea to result without jumping between separate tools.",
      points: [
        "Browse public templates before you commit to a direction.",
        "Edit the source frame inside the same workflow.",
        "Generate stills or short clips from the same setup.",
      ],
    },
    {
      label: "What Keeps It Useful",
      title: "Build around scenes you want to repeat",
      intro:
        "A strong NSFW AI workflow is not one lucky result. It is a setup you can return to whenever you want new stills, new clips, or another variation of the same idea.",
      points: [
        "Save the looks and scenes that perform best for you.",
        "Use repeatable templates instead of gambling on random prompts.",
        "Treat every strong result as a starting point for the next one.",
      ],
    },
  ],
  girlfriend: [
    {
      label: "Why It Feels Better",
      title: "Character consistency matters more than randomness",
      intro:
        "AI girlfriend creation works better when the same look, tone, and persona are easy to revisit. That consistency is what makes the workflow feel personal instead of disposable.",
      points: [
        "Start from a template that already matches the persona you want.",
        "Keep the face, vibe, and styling close across variations.",
        "Use saved setups when you want the same character again.",
      ],
    },
    {
      label: "How People Build It",
      title: "Create the persona once, then keep evolving it",
      intro:
        "The most useful character workflows are the ones you can return to. Build the base persona, refine the details, and then use that same foundation for new scenes and moods.",
      points: [
        "Refine the look before branching into new scenes.",
        "Keep a few winning setups instead of one-off experiments.",
        "Use motion only when the still version already feels right.",
      ],
    },
  ],
  imageGenerator: [
    {
      label: "Why Images Convert Fast",
      title: "A strong still frame gives you more control",
      intro:
        "AI porn image generation usually feels better when you can shape the frame before you ask for motion. A clean still is easier to judge, easier to edit, and easier to reuse later.",
      points: [
        "Browse templates that already match the pose or scene you want.",
        "Use image edits to tighten detail before creating more versions.",
        "Keep the best frame so it can become the base for future clips.",
      ],
    },
    {
      label: "How People Use It",
      title: "Build the image first, then branch into variations",
      intro:
        "Once you have one image that looks right, the rest of the workflow becomes simpler. You can make more stills, explore alternate looks, or move into short-form motion without restarting from zero.",
      points: [
        "Use one winning image as the base for new outputs.",
        "Keep faces, framing, and lighting stable when realism matters.",
        "Treat image generation as the foundation of the wider workflow.",
      ],
    },
  ],
  videoGenerator: [
    {
      label: "Why Video Needs Structure",
      title: "Short clips work best when the source frame is already solid",
      intro:
        "AI porn video generation is easier to control when the motion starts from a clean still image or a template that already has stable framing. That makes the clip feel tighter and less random.",
      points: [
        "Start from a strong still image instead of a loose prompt alone.",
        "Keep the movement short and believable when quality matters.",
        "Use the same setup again once you find a motion flow that works.",
      ],
    },
    {
      label: "What Makes Clips Better",
      title: "Refine before you animate",
      intro:
        "Most weak clips are really weak source images. Tighten the base frame first, then move into motion when the composition, anatomy, and lighting already feel right.",
      points: [
        "Fix the frame before the video step starts.",
        "Keep the strongest first clip and iterate from there.",
        "Use image-to-video as an extension of a good still, not a rescue tool.",
      ],
    },
  ],
  nsfwGenerator: [
    {
      label: "Why One Flow Helps",
      title: "Templates, edits, and output should live together",
      intro:
        "An NSFW AI generator feels better when browsing, refinement, and creation happen in the same place. That makes it easier to move from idea to result without losing the setup that works.",
      points: [
        "Browse examples before committing to a direction.",
        "Refine the source frame inside the same workflow.",
        "Reuse saved setups instead of rebuilding every scene from scratch.",
      ],
    },
    {
      label: "What Users Want",
      title: "A repeatable workflow, not a one-off result",
      intro:
        "The best generator workflows are the ones you can come back to. Once the first output feels right, the product should make it easy to keep exploring the same direction.",
      points: [
        "Keep the setups that produce the best images or clips.",
        "Use short, repeatable steps instead of one giant jump.",
        "Treat every strong result as the base for the next variation.",
      ],
    },
  ],
  nsfwImage: [
    {
      label: "Where Image Quality Starts",
      title: "NSFW AI art looks better when the first frame is intentional",
      intro:
        "For NSFW AI image generation, the first image matters more than people expect. Good framing, consistent lighting, and a strong template usually improve the output faster than prompt overload.",
      points: [
        "Choose image examples that already match the look you want.",
        "Use edits to clean the frame before you create more versions.",
        "Keep the subject and background simple when realism is the goal.",
      ],
    },
    {
      label: "How To Use It",
      title: "Turn one strong image into a reusable setup",
      intro:
        "Once you have a strong image, the page becomes more useful. You can branch into alternate angles, cleaner edits, or short clips without abandoning the winning setup.",
      points: [
        "Start with still images before adding motion.",
        "Use one clean result as the basis for more variations.",
        "Keep your strongest frames for future image or video runs.",
      ],
    },
  ],
  nudeGenerator: [
    {
      label: "Why Nude Workflows Need Control",
      title: "Better nude results usually come from slower edits",
      intro:
        "AI nude generation works better when the image is already close to the final direction. Controlled edits, stable framing, and a clean source make the whole workflow easier to manage.",
      points: [
        "Use a source image that already fits the pose and lighting you want.",
        "Apply compatible edits before asking for motion.",
        "Keep the best still frame so you can build more variations later.",
      ],
    },
    {
      label: "How To Get Better Results",
      title: "Treat video as the last step",
      intro:
        "If the still image does not look right yet, adding motion usually makes the problems more obvious. Improve the base frame first, then move into video when the scene feels stable.",
      points: [
        "Clean the still image before starting video generation.",
        "Use short clips once the image already feels finished.",
        "Reuse successful setups instead of starting over every time.",
      ],
    },
  ],
};

const sharedConversions = {
  generator: {
    kicker: "Try Fapify Free",
    title: "Pick a template, refine the frame, and generate your first result",
    text: "Fapify works best when you start from a public template, clean up the image, and then turn it into the final still or clip.",
    steps: [
      {
        title: "Choose a template",
        text: "Open the live template feed and start from a setup that already fits the scene you want.",
      },
      {
        title: "Refine the image",
        text: "Use compatible editing tools to tighten the frame before you generate the final output.",
      },
      {
        title: "Generate and repeat",
        text: "Create the result, keep the setup that works, and reuse it when you want more variations.",
      },
    ],
    perks: ["Free signup", "Public templates", "Image + video tools"],
    bannerTitle: "Ready to try Fapify?",
    bannerText:
      "Open the app, browse the live template feed, and start generating from a workflow that already matches the page you landed on.",
  },
  undress: {
    kicker: "Try Fapify Free",
    title: "Start with a supported image, refine it, then move into motion",
    text: "The cleanest undress AI flow is simple: choose a supported template, apply compatible edits, and animate the result only after the frame looks right.",
    steps: [
      {
        title: "Pick a supported template",
        text: "Start with an image or template that already has stable framing and a clear subject.",
      },
      {
        title: "Use compatible edits",
        text: "Apply nudify-compatible and image-edit tools where the product supports them.",
      },
      {
        title: "Animate the result",
        text: "Once the frame is working, move into image-to-video for a stronger final result.",
      },
    ],
    perks: ["Free signup", "Supported edit flows", "Image-to-video"],
    bannerTitle: "Ready to try an undress AI workflow?",
    bannerText:
      "Start in Fapify with a supported template, refine the frame, and move into video only when the image is ready.",
  },
  nsfw: {
    kicker: "Try Fapify Free",
    title: "Use one flow for templates, edits, and short NSFW clips",
    text: "Fapify brings public templates, editing, and image-to-video together so you can move from browsing to finished output without switching tools.",
    steps: [
      {
        title: "Browse public templates",
        text: "Find a setup that already matches the mood, pose, or scene you want.",
      },
      {
        title: "Tighten the source",
        text: "Edit the source frame before generation so the final output stays more consistent.",
      },
      {
        title: "Create the final output",
        text: "Generate a still or a short clip, then reuse the setup when you want more versions.",
      },
    ],
    perks: ["Free signup", "Templates", "Reusable workflows"],
    bannerTitle: "Ready to try NSFW AI in Fapify?",
    bannerText:
      "Go straight into the template feed, pick a setup that fits, and move into editing or video from the same product flow.",
  },
  girlfriend: {
    kicker: "Try Fapify Free",
    title: "Build a persona once, then keep reusing the setup",
    text: "AI girlfriend-style creation works better when the look, tone, and generation flow stay easy to repeat across multiple outputs.",
    steps: [
      {
        title: "Pick a starting persona",
        text: "Choose a template or scene that is already close to the character you want to build.",
      },
      {
        title: "Refine the look",
        text: "Adjust the frame and image details until the persona feels consistent.",
      },
      {
        title: "Save and generate again",
        text: "Reuse the setup for new images or clips instead of rebuilding the same idea from scratch.",
      },
    ],
    perks: ["Free signup", "Saved setups", "Repeatable character flows"],
    bannerTitle: "Ready to start an AI girlfriend workflow?",
    bannerText:
      "Open Fapify, choose a strong starting template, and build a persona you can return to whenever you want new results.",
  },
  imageGenerator: {
    kicker: "Try Fapify Free",
    title: "Start from a strong image, then refine and reuse it",
    text: "Fapify works well for AI porn image generation because templates, edits, and next-step creation all connect inside the same flow.",
    steps: [
      {
        title: "Choose the closest template",
        text: "Start from a setup that already matches the image style, pose, or framing you want.",
      },
      {
        title: "Refine the frame",
        text: "Use compatible image tools to clean up the result before you branch into more versions.",
      },
      {
        title: "Save the winner",
        text: "Keep the strongest image so it can become the base for more stills or future clips.",
      },
    ],
    perks: ["Free signup", "Image-first workflow", "Reusable results"],
    bannerTitle: "Ready to generate AI porn images?",
    bannerText:
      "Open Fapify, start from a live template, refine the frame, and keep the image that works best as your next starting point.",
  },
  videoGenerator: {
    kicker: "Try Fapify Free",
    title: "Start from a stable frame, then turn it into motion",
    text: "Fapify connects templates, image cleanup, and image-to-video so AI porn video generation feels more controlled from the first clip.",
    steps: [
      {
        title: "Pick a clean starting frame",
        text: "Choose a template or image that already has stable framing, lighting, and subject focus.",
      },
      {
        title: "Tighten the image first",
        text: "Use editing to improve the still image before you ask the model to animate it.",
      },
      {
        title: "Generate a short clip",
        text: "Create motion from the strong still and reuse that setup once you find a result worth keeping.",
      },
    ],
    perks: ["Free signup", "Image-to-video", "Short clip workflows"],
    bannerTitle: "Ready to create AI porn videos?",
    bannerText:
      "Start with a live template, refine the image, and turn the strongest frame into a short clip inside the same product flow.",
  },
  nsfwGenerator: {
    kicker: "Try Fapify Free",
    title: "Browse, refine, and generate without switching tools",
    text: "Fapify gives NSFW AI generator users one path from public templates to finished images or short clips.",
    steps: [
      {
        title: "Browse the live feed",
        text: "Pick a public template that already fits the look, mood, or scene you want to build from.",
      },
      {
        title: "Refine the setup",
        text: "Adjust the source image before generating so your final output stays more consistent.",
      },
      {
        title: "Create and repeat",
        text: "Generate the result, keep the setup that works, and use it again for more variations.",
      },
    ],
    perks: ["Free signup", "Templates + edits", "Repeatable flows"],
    bannerTitle: "Ready to try an NSFW AI generator workflow?",
    bannerText:
      "Open Fapify, browse public templates, and move straight into image creation or short-form motion from the same setup.",
  },
  nsfwImage: {
    kicker: "Try Fapify Free",
    title: "Build better NSFW AI images from a cleaner starting point",
    text: "Fapify helps with NSFW AI art and image generation by combining templates, image editing, and reusable creation flows.",
    steps: [
      {
        title: "Pick the closest image setup",
        text: "Start from a public example that already matches the look, angle, or composition you want.",
      },
      {
        title: "Tighten the still image",
        text: "Use compatible editing tools to improve the frame before you create more variations.",
      },
      {
        title: "Reuse the strongest image",
        text: "Keep the best result and turn it into your base for new images or later video work.",
      },
    ],
    perks: ["Free signup", "NSFW AI art", "Image generator flow"],
    bannerTitle: "Ready to create NSFW AI images?",
    bannerText:
      "Start inside Fapify with a strong template, improve the image, and keep the best frame for future generations.",
  },
  nudeGenerator: {
    kicker: "Try Fapify Free",
    title: "Start from a supported image and build the result step by step",
    text: "Fapify makes AI nude generation easier by keeping templates, compatible edits, and optional motion in one connected workflow.",
    steps: [
      {
        title: "Choose a supported image",
        text: "Start from a source frame or template that already has clear subject focus and stable composition.",
      },
      {
        title: "Apply controlled edits",
        text: "Use compatible image tools to refine the scene before you ask for more output.",
      },
      {
        title: "Generate more from the same setup",
        text: "Keep the best still and reuse it for more images or short clips once the frame feels right.",
      },
    ],
    perks: ["Free signup", "Controlled edits", "Image + video flow"],
    bannerTitle: "Ready to try an AI nude generator workflow?",
    bannerText:
      "Open Fapify, choose a supported template, refine the image, and build new results from a setup that already works.",
  },
};

const sharedFaqs = {
  generator: [
    {
      question: "Is Fapify an AI porn generator?",
      answer:
        "Yes. Fapify offers AI porn generation workflows built around reusable templates, image-to-video creation, and repeatable NSFW creation flows.",
    },
    {
      question: "Can I make porn-style videos from images?",
      answer:
        "Yes. Fapify supports image-to-video generation on supported templates, with options for extending compatible videos.",
    },
    {
      question: "Does Fapify require a prompt every time?",
      answer:
        "Not always. Public templates reduce prompt friction, while advanced settings are available when you need more control.",
    },
  ],
  undress: [
    {
      question: "Does Fapify support undress AI workflows?",
      answer:
        "Fapify includes compatible nudify and edit-image flows on supported templates. Use them only for consenting adult content you are authorized to upload.",
    },
    {
      question: "What is the difference between AI nude and AI naked?",
      answer:
        "They are closely related. In Fapify, both map to supported nudify and edit-image features rather than separate products.",
    },
    {
      question: "Can I animate a nude edit into a video?",
      answer:
        "On supported templates, you can combine editing and image-to-video workflows for a broader AI nude generation process.",
    },
  ],
  nsfw: [
    {
      question: "What does NSFW AI mean on Fapify?",
      answer:
        "It covers the broader AI porn generation stack: templates, AI image editing, image-to-video workflows, and reusable creation settings.",
    },
    {
      question: "Can I browse examples before generating?",
      answer:
        "Yes. Fapify surfaces public templates and examples so visitors can understand the style and workflow before creating.",
    },
    {
      question: "Does Fapify support both images and videos?",
      answer:
        "Yes. Fapify supports AI porn image editing and NSFW video generation workflows.",
    },
  ],
  girlfriend: [
    {
      question: "Can Fapify work for AI girlfriend-style creation?",
      answer:
        "Yes. It works best with reusable persona-style template workflows and repeatable AI porn generation paths built from public templates and saved settings.",
    },
    {
      question: "Do I need a new setup every time?",
      answer:
        "No. Template-based flows reduce setup friction and make recurring AI girlfriend-style generation easier to repeat.",
    },
    {
      question: "Is AI girlfriend content the same as public templates?",
      answer:
        "Not exactly. Public templates help with discovery, while persona-focused workflows are better when you want repeatable character-style creation.",
    },
  ],
  imageGenerator: [
    {
      question: "Can Fapify work as an AI porn image generator?",
      answer:
        "Yes. Fapify supports AI porn image workflows through public templates, compatible image editing, and reusable creation paths.",
    },
    {
      question: "What helps AI porn images look better?",
      answer:
        "Stable framing, cleaner source images, and template-based workflows usually improve the result faster than starting from a blank prompt.",
    },
    {
      question: "Can I turn an image into a video later?",
      answer:
        "Yes. On supported flows, a strong still image can become the base for image-to-video generation afterward.",
    },
  ],
  videoGenerator: [
    {
      question: "Does Fapify support AI porn video generation?",
      answer:
        "Yes. Fapify supports image-to-video workflows on supported templates, along with the image cleanup steps that usually make clips look better.",
    },
    {
      question: "Why do short clips usually look better?",
      answer:
        "Short clips are easier to keep consistent, especially when the source frame is already strong before motion starts.",
    },
    {
      question: "What is the best starting point for AI porn videos?",
      answer:
        "A clean still image or template with stable lighting, pose, and composition usually gives the best base for motion.",
    },
  ],
  nsfwGenerator: [
    {
      question: "Is Fapify an NSFW AI generator?",
      answer:
        "Yes. Fapify supports NSFW AI workflows through templates, compatible image editing, still-image generation, and short-form video creation.",
    },
    {
      question: "What makes an NSFW AI generator easier to use?",
      answer:
        "Public examples, reusable setups, and a clean path from browsing to generation usually make the biggest difference.",
    },
    {
      question: "Can one workflow handle both images and clips?",
      answer:
        "Yes. In Fapify, supported paths can move from images into short clips without forcing you to restart from scratch.",
    },
  ],
  nsfwImage: [
    {
      question: "Can Fapify help with NSFW AI art and image generation?",
      answer:
        "Yes. Fapify supports NSFW AI image workflows through templates, compatible image editing, and repeatable image-first creation paths.",
    },
    {
      question: "What helps NSFW AI images look more realistic?",
      answer:
        "Better source images, stable framing, cleaner lighting, and image refinement before motion all improve realism.",
    },
    {
      question: "Do I need video tools to use this page?",
      answer:
        "No. This workflow can stay image-first. Video is optional when you want to turn a strong still into motion later.",
    },
  ],
  nudeGenerator: [
    {
      question: "Does Fapify support AI nude generator workflows?",
      answer:
        "Yes. Fapify supports AI nude workflows on supported templates through compatible edits, still-image generation, and optional image-to-video steps.",
    },
    {
      question: "Is nude AI different from AI nude?",
      answer:
        "Not in practice. They usually describe the same kind of workflow, which is why they can map to the same product path.",
    },
    {
      question: "What makes AI nude results cleaner?",
      answer:
        "A stronger source image, controlled edits, and treating motion as a later step usually produce better output.",
    },
  ],
};

export const landingPages = [
  {
    path: "/ai-porn-generator",
    title: "AI Porn Generator for NSFW Videos and Custom Templates",
    description:
      "Fapify combines public AI porn templates, image-to-video generation, nudify-compatible edits, and reusable NSFW workflows for AI porn creation.",
    h1: "AI Porn Generator for Images, Clips, and Templates",
    intro:
      "Create AI porn faster with reusable templates, cleaner image edits, and short video workflows that start from a strong frame.",
    supportingCopy:
      "Fapify is built for users who want to browse, refine, and generate in one connected flow instead of fighting a blank prompt box.",
    topics: ["AI Porn", "AI Porn Generator", "Porn Generator", "Make Porn"],
    conversion: sharedConversions.generator,
    sections: sharedSections.generator,
    faq: sharedFaqs.generator,
    relatedLinks: [
      { label: "Explore templates", href: "/" },
      { label: "Undress AI", href: "/undress-ai" },
      { label: "NSFW AI", href: "/nsfw-ai" },
      { label: "AI Girlfriend", href: "/ai-girlfriend" },
    ],
    keywords: ["AI porn", "AI porn generator", "porn generator", "make porn"],
  },
  {
    path: "/ai-porn",
    title: "AI Porn Templates and NSFW Generation Workflows",
    description:
      "Browse Fapify AI porn workflows built around public templates, AI porn image editing, image-to-video tools, and repeatable NSFW creation settings.",
    h1: "AI Porn Templates and Generation Workflows",
    intro:
      "AI porn workflows are easier when you can start from examples, shape the image, and then turn the best frame into motion.",
    supportingCopy:
      "This page is for users who want a clearer path into templates, editing, and repeatable generation.",
    topics: ["AI Porn", "Templates", "Image Editing", "Image-to-Video"],
    conversion: sharedConversions.generator,
    sections: sharedSections.generator,
    faq: sharedFaqs.generator,
    relatedLinks: [
      { label: "AI Porn Generator", href: "/ai-porn-generator" },
      { label: "Make porn", href: "/make-porn" },
      { label: "Porn generator", href: "/porn-generator" },
    ],
    keywords: ["AI porn", "AI porn generator"],
  },
  {
    path: "/porn-generator",
    title: "Porn Generator Templates for AI Porn Images and Videos",
    description:
      "Find templates, image editing, and image-to-video tools for creating AI porn images and videos in one place.",
    h1: "Porn Generator for AI Images and Clips",
    intro:
      "If you want a direct path into AI porn creation, start with templates, edit the still frame, and then move into clips only when the image is ready.",
    supportingCopy:
      "Fapify keeps browsing, editing, and generation connected so the workflow stays easy to repeat.",
    topics: ["Porn Generator", "AI Porn Images", "AI Porn Videos"],
    conversion: sharedConversions.generator,
    sections: sharedSections.generator,
    faq: sharedFaqs.generator,
    relatedLinks: [
      { label: "AI porn generator", href: "/ai-porn-generator" },
      { label: "Explore templates", href: "/" },
    ],
    keywords: ["porn generator", "AI porn generator"],
  },
  {
    path: "/make-porn",
    title: "Make Porn with AI Porn Templates and Image-to-Video",
    description:
      "Fapify supports make porn workflows through reusable AI porn templates, image-to-video generation, and repeatable creator setups.",
    h1: "Make Porn with Templates and Motion",
    intro:
      "If you want to make porn with AI, the cleanest path is simple: start from a template, improve the image, then generate the final still or clip.",
    supportingCopy:
      "The product works best when you treat the workflow as a sequence instead of trying to force everything in one step.",
    topics: ["Make Porn", "Reusable Templates", "Image-to-Video"],
    conversion: sharedConversions.generator,
    sections: sharedSections.generator,
    faq: sharedFaqs.generator,
    relatedLinks: [
      { label: "AI porn generator", href: "/ai-porn-generator" },
      { label: "Undress AI", href: "/undress-ai" },
    ],
    keywords: ["make porn", "AI porn generator"],
  },
  {
    path: "/undress-ai",
    title: "Undress AI and AI Nude Editing Workflows",
    description:
      "Fapify supports undress AI workflows with compatible nudify flows, edit-image tools, and follow-on image-to-video generation on supported templates.",
    h1: "Undress AI, AI Nude, and Image Editing",
    intro:
      "Use undress AI workflows to refine a strong source image, clean the frame, and move into motion only when the still version already looks right.",
    supportingCopy:
      "Fapify combines compatible edits, reusable templates, and image-to-video in one flow so the result feels cleaner and easier to repeat.",
    topics: ["Undress AI", "AI Nude", "AI Naked", "Image Editing"],
    conversion: sharedConversions.undress,
    sections: sharedSections.undress,
    faq: sharedFaqs.undress,
    relatedLinks: [
      { label: "AI naked", href: "/ai-naked" },
      { label: "AI nude", href: "/ai-nude" },
      { label: "AI porn generator", href: "/ai-porn-generator" },
    ],
    keywords: ["undress ai", "ai naked", "naked ai", "nudify ai", "ai nude"],
  },
  {
    path: "/ai-naked",
    canonicalPath: "/undress-ai",
    title: "AI Naked Image Editing and Nudify-Compatible Templates",
    description:
      "Fapify covers AI naked workflows through compatible nudify flows, AI porn image editing, and connected NSFW generation templates.",
    h1: "AI Naked Edits and Templates",
    intro:
      "AI naked workflows usually start with a clean source image, move through careful edits, and branch into generation only after the frame feels stable.",
    supportingCopy:
      "This page focuses on the part users actually care about: getting from a usable image to a better final result without wasting steps.",
    topics: ["AI Naked", "Undress AI", "Nudify-Compatible Edits"],
    conversion: sharedConversions.undress,
    sections: sharedSections.undress,
    faq: sharedFaqs.undress,
    relatedLinks: [
      { label: "Undress AI", href: "/undress-ai" },
      { label: "AI nude", href: "/ai-nude" },
    ],
    keywords: ["ai naked", "naked ai", "undress ai"],
  },
  {
    path: "/ai-nude",
    title: "AI Nude Templates, Nudify-Compatible Edits, and NSFW Motion",
    description:
      "Fapify addresses AI nude intent with AI porn image editing, supported nudify workflows, and related image-to-video generation paths.",
    h1: "AI Nude Templates, Edits, and Motion",
    intro:
      "AI nude workflows work best when the source frame already looks close, the edit feels controlled, and motion only comes after the still image is strong.",
    supportingCopy:
      "Fapify is designed for that order: template first, edit second, motion third.",
    topics: ["AI Nude", "Templates", "Editing", "Motion"],
    conversion: sharedConversions.undress,
    sections: sharedSections.undress,
    faq: sharedFaqs.undress,
    relatedLinks: [
      { label: "Undress AI", href: "/undress-ai" },
      { label: "AI porn generator", href: "/ai-porn-generator" },
    ],
    keywords: ["ai nude", "ai nudes", "undress ai"],
  },
  {
    path: "/nsfw-ai",
    title: "NSFW AI Templates for AI Porn Images, Videos, and Workflows",
    description:
      "Explore NSFW AI templates, image editing, image-to-video generation, and reusable creation tools in Fapify.",
    h1: "NSFW AI for Images, Clips, and Characters",
    intro:
      "NSFW AI is not one button. It is a full flow that starts with templates, improves the source image, and then turns the best results into clips or repeatable scenes.",
    supportingCopy:
      "Fapify keeps that flow connected so you can move from browsing to generation without losing momentum.",
    topics: ["NSFW AI", "AI Porn Images", "AI Porn Videos", "Reusable Personas"],
    conversion: sharedConversions.nsfw,
    sections: sharedSections.nsfw,
    faq: sharedFaqs.nsfw,
    relatedLinks: [
      { label: "AI porn generator", href: "/ai-porn-generator" },
      { label: "Undress AI", href: "/undress-ai" },
      { label: "AI girlfriend", href: "/ai-girlfriend" },
    ],
    keywords: ["nsfw ai", "ai porn", "nsfw ai video generator"],
  },
  {
    path: "/ai-girlfriend",
    title: "AI Girlfriend Templates and Repeatable AI Porn Workflows",
    description:
      "Fapify supports AI girlfriend workflows through reusable templates and AI porn generation paths that can be repeated after setup.",
    h1: "AI Girlfriend Templates and Character Workflows",
    intro:
      "AI girlfriend creation feels better when the character stays recognizable across scenes, moods, and new outputs.",
    supportingCopy:
      "Fapify is built around reusable character setups, saved looks, and generation flows you can return to whenever you want more.",
    topics: ["AI Girlfriend", "Repeatable Workflows", "Saved Setups"],
    conversion: sharedConversions.girlfriend,
    sections: sharedSections.girlfriend,
    faq: sharedFaqs.girlfriend,
    relatedLinks: [
      { label: "Explore templates", href: "/" },
      { label: "AI porn generator", href: "/ai-porn-generator" },
      { label: "NSFW AI", href: "/nsfw-ai" },
    ],
    keywords: ["ai girlfriend", "ai porn", "nsfw ai"],
  },
  {
    path: "/ai-sex",
    title: "AI Sex Workflows for Images, Clips, and NSFW Templates",
    description:
      "Explore AI sex workflows in Fapify through public templates, still-image refinement, short-form clip generation, and repeatable NSFW creation paths.",
    h1: "AI Sex Workflows for Images, Clips, and Templates",
    intro:
      "If you want AI sex creation that feels easier to control, start with a strong template, tighten the image, and move into motion only when the frame is ready.",
    supportingCopy:
      "Fapify connects templates, edits, still-image generation, and short-form motion so the workflow feels more usable from the first session.",
    topics: ["AI Sex", "NSFW Templates", "AI Clips", "AI Images"],
    conversion: sharedConversions.nsfw,
    sections: sharedSections.nsfw,
    faq: sharedFaqs.nsfw,
    relatedLinks: [
      { label: "NSFW AI", href: "/nsfw-ai" },
      { label: "AI porn generator", href: "/ai-porn-generator" },
      { label: "AI porn video generator", href: "/ai-porn-video-generator" },
    ],
    keywords: ["ai sex", "sex ai", "nsfw ai", "ai porn"],
  },
  {
    path: "/nudify-ai",
    title: "Nudify AI Workflows for Images, Edits, and Motion",
    description:
      "Use Fapify for nudify AI workflows with supported templates, compatible image edits, still-image refinement, and optional motion-ready outputs.",
    h1: "Nudify AI Workflows for Images, Edits, and Motion",
    intro:
      "Nudify AI results are easier to improve when the source image already works, the edits stay controlled, and motion only comes after the still frame is strong.",
    supportingCopy:
      "Fapify keeps supported templates, compatible image editing, and optional image-to-video in one connected workflow.",
    topics: ["Nudify AI", "Undress AI", "AI Nude", "Image Editing"],
    conversion: sharedConversions.undress,
    sections: sharedSections.undress,
    faq: sharedFaqs.undress,
    relatedLinks: [
      { label: "Undress AI", href: "/undress-ai" },
      { label: "AI nude", href: "/ai-nude" },
      { label: "AI nude generator", href: "/ai-nude-generator" },
    ],
    keywords: ["nudify ai", "undress ai", "ai nude", "ai naked"],
  },
  {
    path: "/ai-porn-image-generator",
    title: "AI Porn Image Generator for Pics, Templates, and NSFW Edits",
    description:
      "Create AI porn images in Fapify with public templates, compatible image editing, reusable still-image workflows, and optional motion-ready setups.",
    h1: "AI Porn Image Generator for Templates, Pics, and Edits",
    intro:
      "Build AI porn images from templates that already work, improve the frame, and keep the strongest still as the base for more generations.",
    supportingCopy:
      "Fapify keeps image creation simple by connecting public examples, image edits, and repeatable generation paths in one place.",
    topics: [
      "AI Porn Image Generator",
      "AI Porn Pics",
      "AI Image Generator Porn",
      "Porn AI Image Generator",
    ],
    conversion: sharedConversions.imageGenerator,
    sections: sharedSections.imageGenerator,
    faq: sharedFaqs.imageGenerator,
    relatedLinks: [
      { label: "AI porn generator", href: "/ai-porn-generator" },
      { label: "AI porn video generator", href: "/ai-porn-video-generator" },
      { label: "NSFW AI image generator", href: "/nsfw-ai-image-generator" },
    ],
    keywords: [
      "ai porn image generator",
      "ai image generator porn",
      "porn ai image generator",
      "ai porn pics",
    ],
  },
  {
    path: "/ai-porn-video-generator",
    title: "AI Porn Video Generator for NSFW Clips and Image-to-Video",
    description:
      "Create AI porn videos with image-to-video workflows, reusable templates, and short-form NSFW clip generation inside Fapify.",
    h1: "AI Porn Video Generator for Clips, Motion, and Templates",
    intro:
      "AI porn videos look better when you begin with a strong still frame, tighten the image, and only then move into motion.",
    supportingCopy:
      "Fapify connects templates, image cleanup, and image-to-video so the whole clip workflow stays easier to repeat.",
    topics: [
      "AI Porn Videos",
      "AI Porn Video Generator",
      "AI Video Porn",
      "AI Video Porn Generator",
    ],
    conversion: sharedConversions.videoGenerator,
    sections: sharedSections.videoGenerator,
    faq: sharedFaqs.videoGenerator,
    relatedLinks: [
      { label: "AI porn image generator", href: "/ai-porn-image-generator" },
      { label: "AI porn generator", href: "/ai-porn-generator" },
      { label: "NSFW AI", href: "/nsfw-ai" },
    ],
    keywords: [
      "ai porn videos",
      "ai porn video generator",
      "ai video porn",
      "ai video porn generator",
    ],
  },
  {
    path: "/nsfw-ai-generator",
    title: "NSFW AI Generator for Images, Clips, and Templates",
    description:
      "Use Fapify as an NSFW AI generator with public templates, image editing, still-image creation, and short clip workflows in one place.",
    h1: "NSFW AI Generator for Templates, Images, and Clips",
    intro:
      "An NSFW AI generator works best when you can browse examples, refine the image, and create more from the same setup without switching tools.",
    supportingCopy:
      "Fapify is built for that kind of flow, with public templates, connected editing, and repeatable image and clip generation.",
    topics: ["NSFW AI Generator", "AI NSFW", "NSFW AI", "Best NSFW AI"],
    conversion: sharedConversions.nsfwGenerator,
    sections: sharedSections.nsfwGenerator,
    faq: sharedFaqs.nsfwGenerator,
    relatedLinks: [
      { label: "NSFW AI", href: "/nsfw-ai" },
      { label: "NSFW AI image generator", href: "/nsfw-ai-image-generator" },
      { label: "AI porn generator", href: "/ai-porn-generator" },
    ],
    keywords: ["nsfw ai generator", "ai nsfw", "nsfw ai", "best nsfw ai"],
  },
  {
    path: "/nsfw-ai-image-generator",
    title: "NSFW AI Image Generator for Art, Images, and Edits",
    description:
      "Create NSFW AI art and images in Fapify with public templates, image editing, reusable still-image workflows, and motion-ready outputs.",
    h1: "NSFW AI Image Generator for Art, Images, and Templates",
    intro:
      "Use this page when the goal is a stronger NSFW AI image first. Start from a template, refine the still, and keep the best frame for future generations.",
    supportingCopy:
      "Fapify helps with NSFW AI art by making the image workflow easier to control before you branch into motion.",
    topics: [
      "NSFW AI Art",
      "NSFW AI Image",
      "NSFW AI Image Generator",
      "AI Image Generator NSFW",
    ],
    conversion: sharedConversions.nsfwImage,
    sections: sharedSections.nsfwImage,
    faq: sharedFaqs.nsfwImage,
    relatedLinks: [
      { label: "NSFW AI generator", href: "/nsfw-ai-generator" },
      { label: "AI porn image generator", href: "/ai-porn-image-generator" },
      { label: "NSFW AI", href: "/nsfw-ai" },
    ],
    keywords: [
      "nsfw ai art",
      "ai image generator nsfw",
      "nsfw ai image",
      "nsfw ai image generator",
    ],
  },
  {
    path: "/ai-nude-generator",
    title: "AI Nude Generator for Images, Compatible Edits, and Motion",
    description:
      "Create AI nude images in Fapify with supported templates, compatible edit flows, reusable still-image generation, and optional motion workflows.",
    h1: "AI Nude Generator for Images, Edits, and Motion",
    intro:
      "AI nude generation feels cleaner when the source image already works, the edits stay controlled, and motion only comes after the still frame is strong.",
    supportingCopy:
      "Fapify brings supported templates, compatible image editing, and optional image-to-video into one repeatable flow.",
    topics: ["AI Nude Generator", "Nude AI", "Nude AI Generator", "AI Nude"],
    conversion: sharedConversions.nudeGenerator,
    sections: sharedSections.nudeGenerator,
    faq: sharedFaqs.nudeGenerator,
    relatedLinks: [
      { label: "AI nude", href: "/ai-nude" },
      { label: "Undress AI", href: "/undress-ai" },
      { label: "AI porn image generator", href: "/ai-porn-image-generator" },
    ],
    keywords: ["ai nude generator", "nude ai", "nude ai generator", "ai nude"],
  },
  {
    path: "/ai-generated-porn",
    canonicalPath: "/ai-porn-generator",
    title: "AI Generated Porn for Images, Videos, and Templates",
    description:
      "Browse AI generated porn workflows in Fapify through templates, image editing, and image-to-video creation.",
    h1: "AI Generated Porn Workflows",
    intro:
      "AI generated porn works best when templates, edits, and video tools connect in one repeatable flow.",
    supportingCopy:
      "Use this page as a direct path into Fapify's broader AI porn generation workflow.",
    topics: ["AI Generated Porn", "AI Porn", "Templates"],
    conversion: sharedConversions.generator,
    sections: sharedSections.generator,
    faq: sharedFaqs.generator,
    relatedLinks: [
      { label: "AI porn generator", href: "/ai-porn-generator" },
      { label: "AI porn image generator", href: "/ai-porn-image-generator" },
    ],
    keywords: ["ai generated porn", "ai porn"],
  },
  {
    path: "/ai-porn-maker",
    canonicalPath: "/ai-porn-generator",
    title: "AI Porn Maker for Templates, Images, and NSFW Clips",
    description:
      "Use Fapify as an AI porn maker with templates, image editing, and connected image-to-video workflows.",
    h1: "AI Porn Maker Workflows",
    intro:
      "AI porn maker pages work better when the route into templates, edits, and short clips is easy to follow.",
    supportingCopy:
      "This page maps straight to Fapify's main AI porn creation flow.",
    topics: ["AI Porn Maker", "AI Porn Generator", "Templates"],
    conversion: sharedConversions.generator,
    sections: sharedSections.generator,
    faq: sharedFaqs.generator,
    relatedLinks: [
      { label: "AI porn generator", href: "/ai-porn-generator" },
      { label: "Make porn", href: "/make-porn" },
    ],
    keywords: ["ai porn maker", "ai porn generator"],
  },
  {
    path: "/create-ai-porn",
    canonicalPath: "/ai-porn-generator",
    title: "Create AI Porn with Templates, Images, and Motion",
    description:
      "Create AI porn in Fapify by starting from templates, improving the image, and moving into short-form motion when the frame is ready.",
    h1: "Create AI Porn with a Cleaner Workflow",
    intro:
      "The fastest way to create AI porn is to start from a setup that already works, then refine it into the result you want.",
    supportingCopy:
      "Fapify keeps the steps connected so you can browse, edit, and generate from one flow.",
    topics: ["Create AI Porn", "Templates", "Image-to-Video"],
    conversion: sharedConversions.generator,
    sections: sharedSections.generator,
    faq: sharedFaqs.generator,
    relatedLinks: [
      { label: "AI porn generator", href: "/ai-porn-generator" },
      { label: "AI porn video generator", href: "/ai-porn-video-generator" },
    ],
    keywords: ["create ai porn", "ai porn generator"],
  },
  {
    path: "/create-porn",
    canonicalPath: "/make-porn",
    title: "Create Porn with AI Templates and Image-to-Video",
    description:
      "Create porn with AI in Fapify by starting from templates, refining the still image, and moving into short clips when the frame is ready.",
    h1: "Create Porn with AI Templates",
    intro:
      "This route maps to Fapify's template-first workflow for creating adult AI images and short clips.",
    supportingCopy:
      "Start from a working setup, refine the frame, and generate from one connected flow.",
    topics: ["Create Porn", "Make AI Porn", "AI Porn Generator"],
    conversion: sharedConversions.generator,
    sections: sharedSections.generator,
    faq: sharedFaqs.generator,
    relatedLinks: [
      { label: "Make porn", href: "/make-porn" },
      { label: "AI porn generator", href: "/ai-porn-generator" },
    ],
    keywords: ["create porn", "make ai porn", "ai porn generator"],
  },
  {
    path: "/create-porn-ai",
    canonicalPath: "/create-ai-porn",
    title: "Create Porn AI Workflows for Images and Clips",
    description:
      "Use Fapify for create porn AI workflows with templates, image refinement, and image-to-video generation.",
    h1: "Create Porn AI Workflows",
    intro:
      "This route connects create porn AI searches to Fapify's main AI porn generation workflow.",
    supportingCopy:
      "Browse templates, improve the image, and generate more from the same setup.",
    topics: ["Create Porn AI", "Create AI Porn"],
    conversion: sharedConversions.generator,
    sections: sharedSections.generator,
    faq: sharedFaqs.generator,
    relatedLinks: [
      { label: "Create AI porn", href: "/create-ai-porn" },
      { label: "AI porn generator", href: "/ai-porn-generator" },
    ],
    keywords: ["create porn ai", "create ai porn"],
  },
  {
    path: "/make-ai-porn",
    canonicalPath: "/make-porn",
    title: "Make AI Porn with Templates, Images, and Motion",
    description:
      "Make AI porn in Fapify with templates, still-image refinement, and short image-to-video workflows.",
    h1: "Make AI Porn with Templates and Motion",
    intro:
      "This route points to Fapify's make-porn workflow for adult AI images, templates, and short clips.",
    supportingCopy:
      "Use a strong starting frame, improve it, and keep generating from the setup that works.",
    topics: ["Make AI Porn", "AI Porn Maker", "Image-to-Video"],
    conversion: sharedConversions.generator,
    sections: sharedSections.generator,
    faq: sharedFaqs.generator,
    relatedLinks: [
      { label: "Make porn", href: "/make-porn" },
      { label: "AI porn maker", href: "/ai-porn-maker" },
    ],
    keywords: ["make ai porn", "ai porn maker"],
  },
  {
    path: "/ai-image-generator-porn",
    canonicalPath: "/ai-porn-image-generator",
    title: "AI Image Generator Porn Workflows and Templates",
    description:
      "Use Fapify for AI image generator porn workflows with templates, image editing, and reusable still-image creation.",
    h1: "AI Image Generator Porn Workflows",
    intro:
      "This route focuses on the still-image side of AI porn creation, where templates and edits matter most.",
    supportingCopy:
      "Fapify gives you a direct path into image-first adult AI workflows.",
    topics: ["AI Image Generator Porn", "AI Porn Pics"],
    conversion: sharedConversions.imageGenerator,
    sections: sharedSections.imageGenerator,
    faq: sharedFaqs.imageGenerator,
    relatedLinks: [
      { label: "AI porn image generator", href: "/ai-porn-image-generator" },
      { label: "NSFW AI image generator", href: "/nsfw-ai-image-generator" },
    ],
    keywords: ["ai image generator porn", "ai porn image generator"],
  },
  {
    path: "/ai-porn-pics",
    canonicalPath: "/ai-porn-image-generator",
    title: "AI Porn Pics, Templates, and Image Generation",
    description:
      "Browse AI porn pics workflows in Fapify through templates, image refinement, and reusable still-image generation.",
    h1: "AI Porn Pics and Image Workflows",
    intro:
      "If you want stronger still images first, start with templates and refine the frame before creating more variations.",
    supportingCopy:
      "Fapify is designed to make image-first NSFW creation easier to repeat.",
    topics: ["AI Porn Pics", "Templates", "Image Generation"],
    conversion: sharedConversions.imageGenerator,
    sections: sharedSections.imageGenerator,
    faq: sharedFaqs.imageGenerator,
    relatedLinks: [
      { label: "AI porn image generator", href: "/ai-porn-image-generator" },
      { label: "AI porn", href: "/ai-porn" },
    ],
    keywords: ["ai porn pics", "ai porn image generator"],
  },
  {
    path: "/porn-ai-image-generator",
    canonicalPath: "/ai-porn-image-generator",
    title: "Porn AI Image Generator for Templates and Stills",
    description:
      "Use Fapify for porn AI image generator workflows with templates, image editing, and reusable still-image generation.",
    h1: "Porn AI Image Generator Workflows",
    intro:
      "This route points to Fapify's image-first workflow for adult AI stills, templates, and cleaner edits.",
    supportingCopy:
      "Start from a working example and build more from the same frame.",
    topics: ["Porn AI Image Generator", "AI Porn Image Generator"],
    conversion: sharedConversions.imageGenerator,
    sections: sharedSections.imageGenerator,
    faq: sharedFaqs.imageGenerator,
    relatedLinks: [
      { label: "AI porn image generator", href: "/ai-porn-image-generator" },
      { label: "AI porn generator", href: "/ai-porn-generator" },
    ],
    keywords: ["porn ai image generator", "ai porn image generator"],
  },
  {
    path: "/ai-video-porn",
    canonicalPath: "/ai-porn-video-generator",
    title: "AI Video Porn Workflows for Clips and Motion",
    description:
      "Explore AI video porn workflows in Fapify through templates, image cleanup, and short-form motion generation.",
    h1: "AI Video Porn Workflows",
    intro:
      "This route connects directly to Fapify's video-focused AI porn workflow for short clips built from strong still images.",
    supportingCopy:
      "Use it when the goal is motion, not just a single still frame.",
    topics: ["AI Video Porn", "AI Porn Videos"],
    conversion: sharedConversions.videoGenerator,
    sections: sharedSections.videoGenerator,
    faq: sharedFaqs.videoGenerator,
    relatedLinks: [
      { label: "AI porn video generator", href: "/ai-porn-video-generator" },
      { label: "NSFW AI", href: "/nsfw-ai" },
    ],
    keywords: ["ai video porn", "ai porn videos"],
  },
  {
    path: "/ai-video-porn-generator",
    canonicalPath: "/ai-porn-video-generator",
    title: "AI Video Porn Generator for Short NSFW Clips",
    description:
      "Use Fapify as an AI video porn generator with templates, image editing, and image-to-video workflows.",
    h1: "AI Video Porn Generator Workflows",
    intro:
      "This page focuses on building short-form NSFW clips from stronger source images and reusable templates.",
    supportingCopy:
      "Fapify helps you move from still image to motion without rebuilding the workflow from scratch.",
    topics: ["AI Video Porn Generator", "Image-to-Video"],
    conversion: sharedConversions.videoGenerator,
    sections: sharedSections.videoGenerator,
    faq: sharedFaqs.videoGenerator,
    relatedLinks: [
      { label: "AI porn video generator", href: "/ai-porn-video-generator" },
      { label: "AI porn image generator", href: "/ai-porn-image-generator" },
    ],
    keywords: ["ai video porn generator", "ai porn video generator"],
  },
  {
    path: "/ai-nsfw",
    canonicalPath: "/nsfw-ai-generator",
    title: "AI NSFW Generator for Images, Clips, and Templates",
    description:
      "Use Fapify for AI NSFW workflows through templates, image refinement, and short-form clip generation.",
    h1: "AI NSFW Generator Workflows",
    intro:
      "This route maps to Fapify's broader NSFW AI generator flow for templates, images, and short clips.",
    supportingCopy:
      "Browse, refine, and generate from one connected workflow.",
    topics: ["AI NSFW", "NSFW AI Generator"],
    conversion: sharedConversions.nsfwGenerator,
    sections: sharedSections.nsfwGenerator,
    faq: sharedFaqs.nsfwGenerator,
    relatedLinks: [
      { label: "NSFW AI generator", href: "/nsfw-ai-generator" },
      { label: "NSFW AI", href: "/nsfw-ai" },
    ],
    keywords: ["ai nsfw", "nsfw ai generator"],
  },
  {
    path: "/nsfw-ai-art",
    canonicalPath: "/nsfw-ai-image-generator",
    title: "NSFW AI Art, Images, and Generator Workflows",
    description:
      "Browse NSFW AI art workflows in Fapify through templates, still-image generation, and compatible image editing.",
    h1: "NSFW AI Art and Image Workflows",
    intro:
      "This route focuses on the image side of NSFW AI, where templates and still-frame quality make the biggest difference.",
    supportingCopy:
      "Use it to reach Fapify's image-first NSFW AI workflow.",
    topics: ["NSFW AI Art", "NSFW AI Image"],
    conversion: sharedConversions.nsfwImage,
    sections: sharedSections.nsfwImage,
    faq: sharedFaqs.nsfwImage,
    relatedLinks: [
      { label: "NSFW AI image generator", href: "/nsfw-ai-image-generator" },
      { label: "NSFW AI generator", href: "/nsfw-ai-generator" },
    ],
    keywords: ["nsfw ai art", "nsfw ai image generator"],
  },
  {
    path: "/nude-ai",
    canonicalPath: "/ai-nude",
    title: "Nude AI Templates, Images, and Compatible Edits",
    description:
      "Use Fapify for nude AI workflows with supported templates, compatible image edits, and repeatable still-image creation.",
    h1: "Nude AI Workflows and Templates",
    intro:
      "This route connects to Fapify's broader AI nude flow for cleaner still images, compatible edits, and follow-on motion.",
    supportingCopy:
      "Start from a supported template and refine the frame before generating more.",
    topics: ["Nude AI", "AI Nude"],
    conversion: sharedConversions.nudeGenerator,
    sections: sharedSections.nudeGenerator,
    faq: sharedFaqs.nudeGenerator,
    relatedLinks: [
      { label: "AI nude", href: "/ai-nude" },
      { label: "AI nude generator", href: "/ai-nude-generator" },
    ],
    keywords: ["nude ai", "ai nude"],
  },
  {
    path: "/ai-nudes",
    canonicalPath: "/ai-nude",
    title: "AI Nudes Templates, Images, and Compatible Edits",
    description:
      "Use Fapify for AI nudes workflows through supported templates, compatible image editing, and repeatable still-image creation.",
    h1: "AI Nudes Workflows and Templates",
    intro:
      "This route points to Fapify's AI nude workflow for cleaner still images, compatible edits, and optional follow-on motion.",
    supportingCopy:
      "Start from a supported template, refine the frame, and keep the best still for more results later.",
    topics: ["AI Nudes", "AI Nude"],
    conversion: sharedConversions.nudeGenerator,
    sections: sharedSections.nudeGenerator,
    faq: sharedFaqs.nudeGenerator,
    relatedLinks: [
      { label: "AI nude", href: "/ai-nude" },
      { label: "AI nude generator", href: "/ai-nude-generator" },
    ],
    keywords: ["ai nudes", "ai nude"],
  },
  {
    path: "/nude-ai-generator",
    canonicalPath: "/ai-nude-generator",
    title: "Nude AI Generator for Images, Edits, and Motion",
    description:
      "Use Fapify for nude AI generator workflows with templates, compatible image editing, and image-to-video support on supported paths.",
    h1: "Nude AI Generator Workflows",
    intro:
      "This route maps directly to Fapify's generator-style AI nude workflow for supported images and repeatable edits.",
    supportingCopy:
      "Choose a template, refine the frame, and keep the strongest still for more results later.",
    topics: ["Nude AI Generator", "AI Nude Generator"],
    conversion: sharedConversions.nudeGenerator,
    sections: sharedSections.nudeGenerator,
    faq: sharedFaqs.nudeGenerator,
    relatedLinks: [
      { label: "AI nude generator", href: "/ai-nude-generator" },
      { label: "AI nude", href: "/ai-nude" },
    ],
    keywords: ["nude ai generator", "ai nude generator"],
  },
  {
    path: "/sex-ai",
    canonicalPath: "/ai-sex",
    title: "Sex AI Workflows for Images, Clips, and Templates",
    description:
      "Explore sex AI workflows in Fapify through templates, image refinement, and short-form NSFW creation.",
    h1: "Sex AI Workflows",
    intro:
      "This route maps to Fapify's AI sex workflow for templates, images, and short clips.",
    supportingCopy:
      "Browse, refine, and generate from one connected product flow.",
    topics: ["Sex AI", "AI Sex"],
    conversion: sharedConversions.nsfw,
    sections: sharedSections.nsfw,
    faq: sharedFaqs.nsfw,
    relatedLinks: [
      { label: "AI sex", href: "/ai-sex" },
      { label: "NSFW AI", href: "/nsfw-ai" },
    ],
    keywords: ["sex ai", "ai sex"],
  },
  {
    path: "/ai-sex-generator",
    canonicalPath: "/ai-sex",
    title: "AI Sex Generator for Images, Clips, and Templates",
    description:
      "Use Fapify for AI sex generator workflows with templates, image refinement, and short-form adult AI clips.",
    h1: "AI Sex Generator Workflows",
    intro:
      "This route maps to Fapify's AI sex workflow for adult AI images and short clips.",
    supportingCopy:
      "Start with a template, refine the still image, and move into motion when the frame is ready.",
    topics: ["AI Sex Generator", "AI Sex", "AI Sex Video"],
    conversion: sharedConversions.nsfw,
    sections: sharedSections.nsfw,
    faq: sharedFaqs.nsfw,
    relatedLinks: [
      { label: "AI sex", href: "/ai-sex" },
      { label: "AI porn video generator", href: "/ai-porn-video-generator" },
    ],
    keywords: ["ai sex generator", "ai sex", "ai sex video"],
  },
  {
    path: "/ai-sex-video",
    canonicalPath: "/ai-porn-video-generator",
    title: "AI Sex Video Workflows for Short Adult AI Clips",
    description:
      "Create AI sex video workflows in Fapify with templates, still-image cleanup, and image-to-video generation.",
    h1: "AI Sex Video Workflows",
    intro:
      "This route points video-focused AI sex searches to Fapify's image-to-video generation workflow.",
    supportingCopy:
      "Short clips work best when the source frame is already strong and easy to reuse.",
    topics: ["AI Sex Video", "AI Porn Videos", "Image-to-Video"],
    conversion: sharedConversions.videoGenerator,
    sections: sharedSections.videoGenerator,
    faq: sharedFaqs.videoGenerator,
    relatedLinks: [
      { label: "AI porn video generator", href: "/ai-porn-video-generator" },
      { label: "AI sex", href: "/ai-sex" },
    ],
    keywords: ["ai sex video", "ai porn videos"],
  },
  {
    path: "/naked-ai",
    canonicalPath: "/undress-ai",
    title: "Naked AI Workflows for Images, Edits, and Motion",
    description:
      "Use Fapify for naked AI workflows with supported templates, compatible image editing, and optional motion-ready outputs.",
    h1: "Naked AI Workflows",
    intro:
      "This route maps to Fapify's broader undress and AI nude workflow for supported images and controlled edits.",
    supportingCopy:
      "Start from a strong image, refine the frame, and only then move into motion.",
    topics: ["Naked AI", "Undress AI"],
    conversion: sharedConversions.undress,
    sections: sharedSections.undress,
    faq: sharedFaqs.undress,
    relatedLinks: [
      { label: "Undress AI", href: "/undress-ai" },
      { label: "AI nude", href: "/ai-nude" },
    ],
    keywords: ["naked ai", "ai naked", "undress ai"],
  },
  {
    path: "/ai-girlfriend-porn",
    canonicalPath: "/ai-girlfriend",
    title: "AI Girlfriend Porn Templates and Character Workflows",
    description:
      "Explore AI girlfriend porn workflows in Fapify through reusable character templates, image generation, and repeatable persona setups.",
    h1: "AI Girlfriend Porn Workflows",
    intro:
      "This route points to Fapify's character-first workflow for repeatable AI girlfriend-style scenes and outputs.",
    supportingCopy:
      "Use it when consistent persona setup matters more than random one-off generations.",
    topics: ["AI Girlfriend Porn", "AI Girlfriend"],
    conversion: sharedConversions.girlfriend,
    sections: sharedSections.girlfriend,
    faq: sharedFaqs.girlfriend,
    relatedLinks: [
      { label: "AI girlfriend", href: "/ai-girlfriend" },
      { label: "AI porn generator", href: "/ai-porn-generator" },
    ],
    keywords: ["ai girlfriend porn", "ai girlfriend"],
  },
  {
    path: "/porn-ai",
    canonicalPath: "/ai-porn-generator",
    title: "Porn AI Workflows for Images, Videos, and Templates",
    description:
      "Use Fapify for porn AI workflows with templates, image editing, and image-to-video creation.",
    h1: "Porn AI Workflows",
    intro:
      "This route connects to Fapify's main AI porn generation workflow for images, clips, and repeatable templates.",
    supportingCopy:
      "Browse, refine, and generate from one product flow.",
    topics: ["Porn AI", "AI Porn Generator"],
    conversion: sharedConversions.generator,
    sections: sharedSections.generator,
    faq: sharedFaqs.generator,
    relatedLinks: [
      { label: "AI porn generator", href: "/ai-porn-generator" },
      { label: "AI porn", href: "/ai-porn" },
    ],
    keywords: ["porn ai", "porn ai generator"],
  },
  {
    path: "/ai-generator-porn",
    canonicalPath: "/ai-porn-generator",
    title: "AI Generator Porn Templates and Creation Workflows",
    description:
      "Browse AI generator porn workflows in Fapify through templates, image edits, and short-form motion creation.",
    h1: "AI Generator Porn Workflows",
    intro:
      "This route points to Fapify's main AI porn generation flow for templates, images, and clips.",
    supportingCopy:
      "It is a direct way into the product's connected creation workflow.",
    topics: ["AI Generator Porn", "AI Porn Generator"],
    conversion: sharedConversions.generator,
    sections: sharedSections.generator,
    faq: sharedFaqs.generator,
    relatedLinks: [
      { label: "AI porn generator", href: "/ai-porn-generator" },
      { label: "Make porn", href: "/make-porn" },
    ],
    keywords: ["ai generator porn", "ai porn generator"],
  },
  {
    path: "/porn-ai-generator",
    canonicalPath: "/ai-porn-generator",
    title: "Porn AI Generator for Templates, Images, and Clips",
    description:
      "Use Fapify for porn AI generator workflows with templates, image editing, and reusable NSFW creation flows.",
    h1: "Porn AI Generator Workflows",
    intro:
      "This route maps to Fapify's main AI porn generator path for templates, still images, and short clips.",
    supportingCopy:
      "Start from a working example and generate from there.",
    topics: ["Porn AI Generator", "AI Porn Generator"],
    conversion: sharedConversions.generator,
    sections: sharedSections.generator,
    faq: sharedFaqs.generator,
    relatedLinks: [
      { label: "AI porn generator", href: "/ai-porn-generator" },
      { label: "Porn generator", href: "/porn-generator" },
    ],
    keywords: ["porn ai generator", "ai porn generator"],
  },
  {
    path: "/pron-ai-generator",
    canonicalPath: "/ai-porn-generator",
    title: "Pron AI Generator Workflows and Templates",
    description:
      "Use Fapify for pron AI generator searches through templates, image editing, and AI porn generation workflows.",
    h1: "Pron AI Generator Workflows",
    intro:
      "This route maps typo traffic to Fapify's main AI porn generation workflow.",
    supportingCopy:
      "Browse templates, refine the image, and create more from the same setup.",
    topics: ["Pron AI Generator", "AI Porn Generator"],
    conversion: sharedConversions.generator,
    sections: sharedSections.generator,
    faq: sharedFaqs.generator,
    relatedLinks: [
      { label: "AI porn generator", href: "/ai-porn-generator" },
      { label: "AI porn", href: "/ai-porn" },
    ],
    keywords: ["pron ai generator", "ai porn generator"],
  },
];

export const landingPagesByPath = landingPages.reduce((pages, page) => {
  pages[page.path] = page;
  return pages;
}, {});
